import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { customToast } from "../../../../components/notify";
import { IApiPhotoSettings, ICustomFilterItem, IPhotoFilterFormData } from "../../types";
import { photoFilterValidationSchema } from "../../validations/validations";
import { INITIAL_PHOTO_SETTINGS } from "../configs";
import { MAX_CUSTOM_FILTERS } from "../constants";
import { LINE_ID_VALUE } from "../constants/kiosk-constants";
import { mergeKioskSettings } from "../utils/mergeKioskSettings";
import useKioskSettings from "./useKioskSettings";
import { useTabGuard } from "./useTabGuard";
import useUploadAssets from "./useUploadAssets";

interface UsePhotoFilterSettingsProps {
    characterId: string;
}

// 預設濾鏡設定（從 initial-values 取得）
const DEFAULT_PHOTO_FILTER_FORM: IPhotoFilterFormData = {
    enabled_photo: INITIAL_PHOTO_SETTINGS.enabled_photo,
    customized: INITIAL_PHOTO_SETTINGS.filter.customized,
    customFilters: [],
    enable_default: INITIAL_PHOTO_SETTINGS.filter.enable_default,
    take_photo_sec: INITIAL_PHOTO_SETTINGS.take_photo_sec,
    close_result_sec: INITIAL_PHOTO_SETTINGS.close_result_sec,
    character_enable: INITIAL_PHOTO_SETTINGS.character.enable,
    line_enabled: Boolean(INITIAL_PHOTO_SETTINGS.line_id && INITIAL_PHOTO_SETTINGS.line_id.trim() !== ""),
};

export const usePhotoFilterSettings = ({ characterId }: UsePhotoFilterSettingsProps) => {
    const { t } = useTranslation();
    const { kioskSetting, isLoadingKioskSetting, isUpdatingKioskSetting, updateSettings } = useKioskSettings({
        characterId,
    });
    const { handleUploadFile, isUploading } = useUploadAssets();
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [totalFilesToUpload, setTotalFilesToUpload] = useState<number>(0);

    // ======================= 表單設置 =======================
    const methods = useForm<IPhotoFilterFormData>({
        defaultValues: DEFAULT_PHOTO_FILTER_FORM,
        resolver: yupResolver(photoFilterValidationSchema()),
        mode: "onChange",
    });

    const { control, handleSubmit, formState, reset, setValue, getValues } = methods;

    const watchedEnabledPhoto = useWatch({ control, name: "enabled_photo" });
    const watchedCustomized = useWatch({ control, name: "customized" });

    // ======================= Toggle Handler =======================
    const isEnabled = watchedEnabledPhoto;
    const handleToggle = useCallback(
        (enabled: boolean) => {
            setValue("enabled_photo", enabled, { shouldDirty: true });
        },
        [setValue]
    );
    const watchedCustomFilters = useWatch({ control, name: "customFilters" });
    const watchedEnableDefault = useWatch({ control, name: "enable_default" });
    const watchedTakePhotoSec = useWatch({ control, name: "take_photo_sec" });
    const watchedCloseResultSec = useWatch({ control, name: "close_result_sec" });
    const watchedCharacterEnable = useWatch({ control, name: "character_enable" });
    const watchedLineEnabled = useWatch({ control, name: "line_enabled" });

    // ======================= 資料轉換 =======================
    const transformAPItoForm = useCallback((photoData: IApiPhotoSettings | undefined): IPhotoFilterFormData => {
        if (!photoData) {
            return DEFAULT_PHOTO_FILTER_FORM;
        }

        const filter = photoData.filter;

        // 轉換 filters_url 陣列為 customFilters 陣列
        const customFilters: ICustomFilterItem[] = (filter?.filters_url || []).map((url, index) => ({
            key: `custom-filter-${index}-${Date.now()}`,
            url,
            file: null,
        }));

        // 根據 line_id 決定 line_enabled
        const lineEnabled = Boolean(photoData.line_id && photoData.line_id.trim() !== "");

        return {
            enabled_photo: photoData.enabled_photo ?? true,
            customized: filter?.customized ?? false,
            customFilters,
            enable_default: filter?.enable_default ?? true, // Default to true if not set
            take_photo_sec: photoData.take_photo_sec ?? 5,
            close_result_sec: photoData.close_result_sec ?? 20,
            character_enable: photoData.character?.enable ?? false,
            line_enabled: lineEnabled,
        };
    }, []);

    const transformFormToAPI = useCallback((formData: IPhotoFilterFormData): IApiPhotoSettings => {
        const getUrlsFromFilters = (filters: ICustomFilterItem[]) =>
            filters.filter((filter) => filter.url && filter.url.trim() !== "").map((filter) => filter.url);

        return {
            enabled_photo: formData.enabled_photo,
            filter: {
                customized: formData.customized,
                filters_url: getUrlsFromFilters(formData.customFilters),
                enable_default: formData.enable_default,
            },
            character: {
                enable: formData.character_enable,
            },
            line_id: formData.line_enabled ? LINE_ID_VALUE : "",
            take_photo_sec: formData.take_photo_sec,
            close_result_sec: formData.close_result_sec,
            updated_at: new Date().toISOString(), // Track photo filter changes
        };
    }, []);

    // ======================= 檔案處理函數 =======================
    const handleAddFilters = useCallback(
        (files: FileList | File[]) => {
            const currentFilters = getValues("customFilters") as ICustomFilterItem[];
            const remainingSlots = MAX_CUSTOM_FILTERS - currentFilters.length;

            if (remainingSlots <= 0) {
                customToast.error(t("APPWIDGET.PHOTO_FILTER_MAX_FILTERS"));
                return;
            }

            const filesToAdd = Array.from(files).slice(0, remainingSlots);

            const newFilters: ICustomFilterItem[] = filesToAdd.map((file) => ({
                key: uuidv4(),
                url: "",
                file,
            }));

            setValue("customFilters", [...currentFilters, ...newFilters], { shouldDirty: true });

            if (filesToAdd.length < files.length) {
                customToast.warn(
                    t("APPWIDGET.PHOTO_FILTER_PARTIAL_ADD", {
                        added: filesToAdd.length,
                        total: files.length,
                    })
                );
            }
        },
        [getValues, setValue, t]
    );

    const handleRemoveFilter = useCallback(
        (key: string) => {
            const currentFilters = getValues("customFilters") as ICustomFilterItem[];
            const updatedFilters = currentFilters.filter((filter) => filter.key !== key);
            setValue("customFilters", updatedFilters, { shouldDirty: true });
        },
        [getValues, setValue]
    );

    // ======================= 上傳處理函數 =======================
    const uploadPendingFiles = useCallback(
        async (filters: ICustomFilterItem[]): Promise<ICustomFilterItem[]> => {
            const filesToUpload = filters.filter((filter) => filter.file !== null);

            if (filesToUpload.length === 0) {
                return filters;
            }

            setTotalFilesToUpload((prev) => prev + filesToUpload.length);

            const updatedFilters = [...filters];

            for (let i = 0; i < updatedFilters.length; i++) {
                const filter = updatedFilters[i];
                if (filter.file) {
                    const response = await handleUploadFile({
                        file: filter.file,
                        prefix: "image",
                    });

                    if (response?.url) {
                        updatedFilters[i] = {
                            ...filter,
                            url: response.url,
                            file: null,
                        };
                        setUploadProgress((prev) => prev + 1);
                    } else {
                        customToast.error(t("COMMON.UPLOAD_FAILED") || "上傳失敗");
                        throw new Error(`Failed to upload ${filter.file.name}`);
                    }
                }
            }

            return updatedFilters;
        },
        [handleUploadFile, t]
    );

    // ======================= 提交處理函數 =======================
    const onSubmit = useCallback(
        async (data: IPhotoFilterFormData) => {
            try {
                setUploadProgress(0);
                setTotalFilesToUpload(0);

                // Upload pending files if customized is enabled
                if (data.customized && data.customFilters.some((f) => f.file)) {
                    data.customFilters = await uploadPendingFiles(data.customFilters);
                    setValue("customFilters", data.customFilters);
                }

                const apiPayload = transformFormToAPI(data);
                const currentNative = kioskSetting?.data?.native;
                const mergedNative = mergeKioskSettings(currentNative, { photo: apiPayload });

                await updateSettings({ native: mergedNative });

                reset(data);
                customToast.success(t("COMMON.SETTINGS_SAVED") || "設定已儲存");
            } catch (error) {
                customToast.error(t("COMMON.SETTINGS_ERROR") || "儲存設定失敗");
            } finally {
                setUploadProgress(0);
                setTotalFilesToUpload(0);
            }
        },
        [uploadPendingFiles, transformFormToAPI, kioskSetting, updateSettings, setValue, reset, t]
    );

    const saveHandler = useCallback(async () => {
        await handleSubmit(onSubmit)();
    }, [handleSubmit, onSubmit]);

    useTabGuard("photoFilter", formState.isDirty, saveHandler);

    // ======================= 初始化表單從 API =======================
    useEffect(() => {
        // Only reset form when loading completes to avoid race conditions
        if (!isLoadingKioskSetting) {
            const photoData = kioskSetting?.data?.native?.photo;
            const formData = transformAPItoForm(photoData);
            reset(formData);
        }
    }, [kioskSetting?.data?.native?.photo, isLoadingKioskSetting, reset, transformAPItoForm]);

    // ======================= Computed Values =======================
    const canAddMoreFilters = () => {
        const filters = getValues("customFilters") as ICustomFilterItem[];
        return (filters?.length || 0) < MAX_CUSTOM_FILTERS;
    };

    /**
     * Validate that filters have valid content
     * When customized is OFF: always valid (using default filters)
     * When customized is ON: need at least one custom filter OR enable_default must be true
     */
    const hasValidFilters = useMemo(() => {
        if (!watchedCustomized) {
            // When customized is OFF, always valid (using all default filters)
            return true;
        }

        // When customized is ON, need at least one filter (custom or enable_default)
        const hasCustomFilters =
            watchedCustomFilters?.length > 0 &&
            watchedCustomFilters.some((f) => f.file || (f.url && f.url.trim() !== ""));
        const hasDefaultFilters = watchedEnableDefault;

        return hasCustomFilters || hasDefaultFilters;
    }, [watchedCustomized, watchedCustomFilters, watchedEnableDefault]);

    return {
        // Form methods
        methods,
        handleSubmit,
        onSubmit,
        formState,
        isEnabled,
        handleToggle,

        // Watched values
        watchedEnabledPhoto,
        watchedCustomized,
        watchedCustomFilters,
        watchedEnableDefault,
        watchedTakePhotoSec,
        watchedCloseResultSec,
        watchedCharacterEnable,
        watchedLineEnabled,

        // Computed values
        canAddMoreFilters,
        hasValidFilters,

        // Upload state
        uploadProgress,
        totalFilesToUpload,

        // Loading states
        isLoadingKioskSetting,
        isUpdatingKioskSetting,
        isUploading,

        // Handlers
        handleAddFilters,
        handleRemoveFilter,

        // Constants
        MAX_CUSTOM_FILTERS,
    };
};

export default usePhotoFilterSettings;
